function updateAbbHeaderHeight() {
  const header = document.querySelector('[data-abb-header]');
  if (!header) return;
  const rect = header.getBoundingClientRect();
  document.documentElement.style.setProperty('--header-height', `${rect.height}px`);
  document.documentElement.style.setProperty('--abb-header-bottom', `${rect.bottom}px`);
}

class AbbMegaMenu {
  constructor() {
    this.items = document.querySelectorAll('[data-abb-mega-item]');
    this.overlay = document.querySelector('[data-abb-mega-overlay]');
    this.desktopQuery = window.matchMedia('(min-width: 990px)');
    this.hoverTimer = null;
    this.closeTimer = null;
    this.lastPointerX = 0;
    this.lastPointerY = 0;

    if (!this.items.length) return;

    this.headerEl = document.querySelector('[data-abb-header]');
    this.updateHeaderHeight();

    if (this.headerEl && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(() => this.updateHeaderHeight());
      observer.observe(this.headerEl);
    }

    window.addEventListener('resize', () => this.updateHeaderHeight());
    window.addEventListener(
      'scroll',
      () => requestAnimationFrame(() => this.updateHeaderHeight()),
      { passive: true }
    );

    document.addEventListener(
      'pointermove',
      (event) => {
        this.lastPointerX = event.clientX;
        this.lastPointerY = event.clientY;
      },
      { passive: true }
    );

    this.items.forEach((item) => this.bindItem(item));
    this.bindParentLinks();
    this.bindDocumentClose();
    this.overlay?.addEventListener('click', () => this.closeAll());
    this.overlay?.addEventListener('mouseenter', () => this.cancelClose());
    this.overlay?.addEventListener('mouseleave', () => this.scheduleClose());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll();
    });
  }

  isMegaSafeTarget(target) {
    if (!(target instanceof Element)) return false;

    return !!target.closest(
      '[data-abb-mega-item], .abb-mega__panel, [data-abb-header], .abb-mega-overlay'
    );
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
  }

  scheduleClose() {
    if (!this.desktopQuery.matches) return;

    this.cancelClose();
    this.closeTimer = setTimeout(() => {
      const hovered = document.elementFromPoint(this.lastPointerX, this.lastPointerY);
      if (!this.isMegaSafeTarget(hovered)) {
        this.closeAll();
      }
    }, 250);
  }

  bindItem(item) {
    const details = item.querySelector('details');
    if (!details) return;

    const summary = item.querySelector('summary');
    const panel = item.querySelector('.abb-mega__panel');

    summary?.addEventListener('click', (event) => {
      if (!this.desktopQuery.matches) return;
      if (event.target.closest('[data-abb-mega-parent-link]')) return;
      event.preventDefault();
      if (details.open) {
        this.closeItem(item, details);
      } else {
        this.openItem(item, details);
      }
    });

    const onEnter = () => {
      if (!this.desktopQuery.matches) return;
      this.cancelClose();
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => this.openItem(item, details), 80);
    };

    const onLeave = () => {
      if (!this.desktopQuery.matches) return;
      clearTimeout(this.hoverTimer);
      this.scheduleClose();
    };

    item.addEventListener('mouseenter', onEnter);
    item.addEventListener('mouseleave', onLeave);
    panel?.addEventListener('mouseenter', onEnter);
    panel?.addEventListener('mouseleave', onLeave);
  }

  bindParentLinks() {
    document.querySelectorAll('[data-abb-mega-parent-link]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    });
  }

  bindDocumentClose() {
    document.addEventListener('pointerover', (event) => {
      if (!this.desktopQuery.matches || !this.hasOpenMenu()) return;

      this.lastPointerX = event.clientX;
      this.lastPointerY = event.clientY;

      if (this.isMegaSafeTarget(event.target)) {
        this.cancelClose();
        return;
      }

      this.scheduleClose();
    });
  }

  updateHeaderHeight() {
    updateAbbHeaderHeight();
  }

  openItem(item, details) {
    this.closeOthers(item);
    this.updateHeaderHeight();
    details.open = true;
    this.overlay?.classList.add('is-active');
    document.documentElement.classList.add('abb-mega-open');
  }

  closeItem(item, details) {
    details.open = false;
    if (!this.hasOpenMenu()) {
      this.overlay?.classList.remove('is-active');
      document.documentElement.classList.remove('abb-mega-open');
    }
  }

  closeOthers(currentItem) {
    this.items.forEach((item) => {
      if (item === currentItem) return;
      const details = item.querySelector('details');
      if (!details) return;
      details.open = false;
    });
  }

  closeAll() {
    clearTimeout(this.hoverTimer);
    clearTimeout(this.closeTimer);
    this.items.forEach((item) => {
      const details = item.querySelector('details');
      if (!details) return;
      details.open = false;
    });
    this.overlay?.classList.remove('is-active');
    document.documentElement.classList.remove('abb-mega-open');
  }

  hasOpenMenu() {
    return Array.from(this.items).some((item) => item.querySelector('details[open]'));
  }
}

class AbbHeaderSearch {
  constructor() {
    this.storageKey = 'abb-recent-searches';
    this.maxRecent = 5;
    this.inputs = document.querySelectorAll('[data-abb-search-input]');
    if (!this.inputs.length) return;

    this.inputs.forEach((input) => this.bindInput(input));

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-abb-header-search], [data-abb-mobile-search], [data-abb-drawer-search]')) {
        this.hideAll();
      }
    });
  }

  getContext(input) {
    const wrapper = input.closest('[data-abb-header-search], [data-abb-mobile-search], [data-abb-drawer-search]');
    const suggestions = wrapper?.querySelector('[data-abb-search-suggestions]');
    const form = input.closest('form');
    const clearBtn = form?.querySelector('[data-abb-search-clear]');
    return { wrapper, suggestions, form, clearBtn };
  }

  syncClearButton(input, clearBtn, suggestions) {
    if (!clearBtn) return;
    const hasValue = input.value.trim().length > 0;
    const suggestionsOpen = suggestions && !suggestions.hidden;
    clearBtn.hidden = !(hasValue || suggestionsOpen);
  }

  clearSearch(input, suggestions, clearBtn) {
    input.value = '';
    if (suggestions) {
      suggestions.hidden = true;
      suggestions.innerHTML = '';
    }
    this.syncClearButton(input, clearBtn, suggestions);
    input.blur();
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  getRecentSearches() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      return Array.isArray(stored) ? stored.filter(Boolean).slice(0, this.maxRecent) : [];
    } catch {
      return [];
    }
  }

  saveRecentSearch(query) {
    const trimmed = query.trim();
    if (!trimmed) return;

    const recent = this.getRecentSearches().filter((item) => item.toLowerCase() !== trimmed.toLowerCase());
    recent.unshift(trimmed);
    localStorage.setItem(this.storageKey, JSON.stringify(recent.slice(0, this.maxRecent)));
  }

  hideAll() {
    document.querySelectorAll('[data-abb-search-suggestions]').forEach((box) => {
      box.hidden = true;
      box.innerHTML = '';
      this.updateClearForSuggestions(box);
    });
  }

  renderResults(suggestions, results) {
    if (!results.length) {
      suggestions.innerHTML = '<p class="abb-header__search-empty">No results found</p>';
      suggestions.hidden = false;
      this.updateClearForSuggestions(suggestions);
      return;
    }

    suggestions.innerHTML = results
      .map((result) => {
        const title = this.escapeHtml(result.title);
        const url = String(result.url || '#').replace(/"/g, '%22');
        return `<a href="${url}">${title}</a>`;
      })
      .join('');

    suggestions.hidden = false;
    this.updateClearForSuggestions(suggestions);
  }

  updateClearForSuggestions(suggestions) {
    const wrapper = suggestions?.closest('[data-abb-header-search], [data-abb-mobile-search], [data-abb-drawer-search]');
    const input = wrapper?.querySelector('[data-abb-search-input]');
    const clearBtn = wrapper?.querySelector('[data-abb-search-clear]');
    if (input) this.syncClearButton(input, clearBtn, suggestions);
  }

  fetchSuggestions(query, suggestions) {
    fetch(
      `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=5`
    )
      .then((response) => response.json())
      .then((data) => {
        const products = data.resources?.results?.products || [];
        const collections = data.resources?.results?.collections || [];
        const pages = data.resources?.results?.pages || [];
        const results = products.concat(collections, pages);

        this.renderResults(suggestions, results);
      })
      .catch(() => {
        suggestions.hidden = true;
        suggestions.innerHTML = '';
        this.updateClearForSuggestions(suggestions);
      });
  }

  showInitialSuggestions(input, suggestions) {
    const query = input.value.trim();

    if (query.length >= 1) {
      this.fetchSuggestions(query, suggestions);
      return;
    }

    const recent = this.getRecentSearches().map((term) => ({
      title: term,
      url: `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(term)}`,
    }));

    if (recent.length) {
      this.renderResults(suggestions, recent);
      return;
    }

    this.fetchSuggestions('k-beauty', suggestions);
  }

  bindInput(input) {
    const { suggestions, form, clearBtn } = this.getContext(input);
    if (!suggestions) return;

    let timer;

    this.syncClearButton(input, clearBtn, suggestions);

    clearBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.clearSearch(input, suggestions, clearBtn);
    });

    input.addEventListener('focus', () => {
      this.showInitialSuggestions(input, suggestions);
      this.syncClearButton(input, clearBtn, suggestions);
    });

    input.addEventListener('click', () => {
      if (suggestions.hidden) {
        this.showInitialSuggestions(input, suggestions);
      }
      this.syncClearButton(input, clearBtn, suggestions);
    });

    input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(timer);
      this.syncClearButton(input, clearBtn, suggestions);

      if (!query) {
        this.showInitialSuggestions(input, suggestions);
        return;
      }

      timer = setTimeout(() => {
        this.fetchSuggestions(query, suggestions);
      }, 220);
    });

    form?.addEventListener('submit', () => {
      this.saveRecentSearch(input.value);
    });
  }
}

class AbbMobileAnnouncement {
  constructor(drawerDetails) {
    this.drawerDetails = drawerDetails;
    this.slot = document.querySelector('[data-abb-announcement-slot]');
    this.headerGroup = document.querySelector('.shopify-section-group-header-group');
    this.sourceSection = document.querySelector(
      '.shopify-section-group-header-group .shopify-section:has(.dcoded-announcement-bar)'
    );

    if (!this.drawerDetails || !this.slot || !this.sourceSection || !this.headerGroup) return;

    this.sync = this.sync.bind(this);
    this.drawerDetails.addEventListener('toggle', this.sync);

    const observer = new MutationObserver(this.sync);
    observer.observe(this.drawerDetails, { attributes: true, attributeFilter: ['class', 'open'] });
  }

  isMenuOpen() {
    return this.drawerDetails.hasAttribute('open') && this.drawerDetails.classList.contains('menu-opening');
  }

  sync() {
    if (this.isMenuOpen()) {
      if (this.sourceSection.parentElement !== this.slot) {
        this.slot.appendChild(this.sourceSection);
      }
      requestAnimationFrame(() => updateAbbHeaderHeight());
      return;
    }

    if (this.sourceSection.parentElement === this.slot) {
      this.headerGroup.insertBefore(this.sourceSection, this.headerGroup.firstChild);
      requestAnimationFrame(() => updateAbbHeaderHeight());
    }
  }
}

class AbbMobileSearch {
  constructor(drawerDetails) {
    this.drawerDetails = drawerDetails;
    this.panel = document.querySelector('[data-abb-mobile-search]');
    this.trigger = document.querySelector('[data-abb-mobile-search-trigger]');
    this.input = this.panel?.querySelector('[data-abb-search-input]');

    if (!this.panel || !this.trigger || !this.input) return;

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.isOpen() ? this.close() : this.open();
    });

    document.addEventListener('click', (event) => {
      if (!this.isOpen()) return;
      if (event.target.closest('[data-abb-mobile-search], [data-abb-mobile-search-trigger]')) return;
      this.close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    });

    this.drawerDetails?.addEventListener('toggle', () => {
      if (this.drawerDetails?.hasAttribute('open')) this.close();
    });
  }

  isOpen() {
    return !this.panel.hidden;
  }

  open() {
    if (this.drawerDetails?.hasAttribute('open')) {
      this.drawerDetails.querySelector('summary')?.click();
    }

    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('abb-mobile-search-open');
    window.requestAnimationFrame(() => {
      updateAbbHeaderHeight();
      this.input.focus();
    });
  }

  close() {
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('abb-mobile-search-open');
    const suggestions = this.panel.querySelector('[data-abb-search-suggestions]');
    if (suggestions) {
      suggestions.hidden = true;
      suggestions.innerHTML = '';
    }
    requestAnimationFrame(() => updateAbbHeaderHeight());
  }
}

class AbbMobileDrawer {
  constructor() {
    this.items = document.querySelectorAll('[data-abb-mobile-item]');
    this.drawerDetails = document.querySelector('#Details-abb-menu-drawer');

    if (this.items.length) {
      this.items.forEach((item) => this.bindAccordionItem(item));
    }

    if (this.drawerDetails) {
      const syncMenuState = () => {
        updateAbbHeaderHeight();
        const isOpen =
          this.drawerDetails.hasAttribute('open') && this.drawerDetails.classList.contains('menu-opening');
        document.documentElement.classList.toggle('abb-mobile-menu-open', isOpen);

        if (!this.drawerDetails.open) {
          this.closeAllAccordions();
          document.documentElement.classList.remove('abb-mobile-menu-open');
        }
      };

      this.drawerDetails.addEventListener('toggle', () => {
        updateAbbHeaderHeight();
        requestAnimationFrame(syncMenuState);
      });

      const observer = new MutationObserver(() => {
        requestAnimationFrame(syncMenuState);
      });

      observer.observe(this.drawerDetails, { attributes: true, attributeFilter: ['class', 'open'] });
    }

    window.addEventListener('resize', () => {
      this.items.forEach((item) => {
        if (item.classList.contains('is-open')) {
          this.setSubmenuHeight(item, true);
        }
      });
    });
  }

  bindAccordionItem(item) {
    const toggle = item.querySelector('[data-abb-mobile-toggle]');
    const submenu = item.querySelector('[data-abb-mobile-sub]');
    if (!toggle || !submenu) return;

    const onToggle = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const isOpen = item.classList.contains('is-open');

      this.closeAllAccordions(item);

      if (isOpen) {
        this.setSubmenuHeight(item, false);
        item.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        this.setSubmenuHeight(item, true);
      }
    };

    toggle.addEventListener('click', onToggle);
    toggle.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle(event);
      }
    });
  }

  setSubmenuHeight(item, open) {
    const submenu = item.querySelector('[data-abb-mobile-sub]');
    if (!submenu) return;

    if (!open) {
      submenu.style.maxHeight = '0px';
      return;
    }

    submenu.style.maxHeight = '0px';
    requestAnimationFrame(() => {
      submenu.style.maxHeight = `${submenu.scrollHeight}px`;
    });
  }

  closeAllAccordions(exceptItem = null) {
    this.items.forEach((item) => {
      if (item === exceptItem) return;
      item.classList.remove('is-open');
      this.setSubmenuHeight(item, false);
      const toggle = item.querySelector('[data-abb-mobile-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AbbMegaMenu();
  new AbbHeaderSearch();
  const drawerDetails = document.querySelector('#Details-abb-menu-drawer');
  new AbbMobileDrawer();
  new AbbMobileAnnouncement(drawerDetails);
  new AbbMobileSearch(drawerDetails);
  updateAbbHeaderHeight();
});
